<script>
    import {modalStore} from '@skeletonlabs/skeleton';
    import determineBuildingImage from '$components/common/determineBuildingImage';
    import ReviewCard from '$components/ReviewCard.svelte';
    import InlineBuildingLabel from '$components/InlineBuildingLabel.svelte';
    import StarRating from '$components/StarRating.svelte';
    import toast from 'svelte-french-toast';
    import UnauthorizedToast from '$components/toasts/UnauthorizedToast.svelte';
    import SEO from '$components/SEO.svelte';
    import {onMount, tick} from 'svelte';
    import {page} from '$app/stores';
    import {PUBLIC_URL} from '$env/static/public';
    import AddReviewCTA from '$components/AddReviewCTA.svelte';
    import BuildingCardV2 from '$components/building-card/BuildingCardV2.svelte';
    import Breadcrumb from '$components/Breadcrumb.svelte';
    import ReferToFriendAside from "$components/building-detail/ReferToFriendAside.svelte";
    import {browser} from "$app/environment";
    import EarlyReviewerBadge from "$components/badges/EarlyReviewerBadge.svelte";
    import MoreReviewsPrompt from "$components/building-detail/MoreReviewsPrompt.svelte";
    import AggregatedReviewScore from "$components/AggregatedReviewScore.svelte";
    import WhereToGoNext from "$components/building-detail/WhereToGoNext.svelte";
    import NotYetCompletedBadge from "$components/badges/NotYetCompletedBadge.svelte";
    import {invalidateAll} from "$app/navigation";

    export let data;
    let reviews = [];
    let reviewRefs = {};
    let highlightedReview = null;
    let buildingImage = {};

    let {
        building,
        buildingScore,
        buildingDetailedScore,
        isAdmin,
        isLoggedIn,
        reviewLikes,
        reviewerNames,
        nearbyBuildings,
        referralCode
    } = data;
    $: ({
        building,
        buildingScore,
        buildingDetailedScore,
        isAdmin,
        isLoggedIn,
        reviewLikes,
        reviewerNames,
        nearbyBuildings,
        referralCode
    } = data);
    $: (reviews = building.reviews || []);
    let SEOStars = '';
    $: {
        for (let i = 0; i < Math.floor(buildingScore?.score || 0); i++) {
            SEOStars += '⭐';
        }
    }

    $: {
        buildingImage = determineBuildingImage(building);
    }

    function showFeedbackToast(message = 'Thank you for your feedback!') {
        toast.success(message, {
            position: 'top-center',
        });
    }

    async function handleLikeReview(e) {
        const res = await fetch('/api/reviews/likes', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({reviewUuid: e.detail.uuid}),
        });
        const {success} = await res.json();
        if (success) {
            showFeedbackToast();

            let existing = reviewLikes[e.detail.uuid] || {count: 0, isLikedByMe: false};

            reviewLikes = {
                ...reviewLikes,
                [e.detail.uuid]: {
                    count: existing.count + 1,
                    isLikedByMe: true,
                },
            };
        } else {
            if (res.status === 401 || res.status === 400) {
                toast(UnauthorizedToast, {
                    position: 'top-center',
                    duration: 5000,
                    icon: '🔒',
                });
            } else if (res.status === 500) {
                toast.error('Something went wrong and we couldn\'t record your feedback. Please try again in a bit.', {
                    position: 'top-center',
                    duration: 5000,
                    icon: '❌',
                });
            }
        }
    }

    async function handleUnlikeReview(e) {
        const res = await fetch('/api/reviews/likes', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({reviewUuid: e.detail.uuid}),
        });
        const {success} = await res.json();
        if (success) {
            showFeedbackToast('Your feedback has been removed.');

            let existing = reviewLikes[e.detail.uuid] || {count: 1, isLikedByMe: true};

            reviewLikes = {
                ...reviewLikes,
                [e.detail.uuid]: {
                    count: existing.count - 1,
                    isLikedByMe: false,
                },
            };
        } else {
            if (res.status === 401 || res.status === 400) {
                toast(UnauthorizedToast, {
                    position: 'top-center',
                    duration: 5000,
                    icon: '🔒',
                });
            } else if (res.status === 500) {
                toast.error('Something went wrong and we couldn\'t record your feedback. Please try again in a bit.', {
                    position: 'top-center',
                    duration: 5000,
                    icon: '❌',
                });
            }
        }
    }

    async function handleReportReview(e) {
        const reviewUuid = e.detail.uuid;

        modalStore.trigger({
            type: 'component',
            component: 'reportReview',
            backdropClasses: '!bg-black !bg-opacity-50',
            meta: {
                reviewUuid,
            },
        });
    }

    async function updateBuildingCompletionStatus(isCompleted) {
        const res = await fetch('/admin/api/buildings/completion-status', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                buildingId: building.uuid,
                isCompleted,
            }),
        });

        const {success} = await res.json();
        if (success) {
            invalidateAll();
        }
    }

    onMount(async () => {
        if ($page.url.searchParams.get('referrer') && browser) {
            const url = new URL(window.location.href);
            // TODO: record that the user came from a referral link
            const referralCode = url.searchParams.get('referrer');
            url.searchParams.delete('referrer');
            history.replaceState({}, '', url);
        }

        if ($page.url.searchParams.get('rid')) {
            await tick();
            highlightedReview = $page.url.searchParams.get('rid');
            reviewRefs[highlightedReview].scrollIntoView({behavior: 'smooth'});
        }
    });
</script>

<SEO title='CondoGenius - {building.name}, {building.address}, {building.neighbourhood} Condo Reviews{SEOStars.length > 0 ? ` - ${SEOStars}` : ""}'
     description='Authentic condo reviews of {building.name} at {building.address}, {building.sub_localities?.name}, Toronto.'
     image='{building.cover_image}'
     breadcrumbs={[{name: building.localities.name, url: `/buildings/${building.localities.slug}`}, {name: building.sub_localities.name, url: `/buildings/${building.localities.slug}/${building.sub_localities.slug}-n${building.sub_localities.id}`}, {name: building.name}]}
/>

<div class='pb-10 flex flex-col'>
    <div>
        <Breadcrumb locality={building.localities} sub_locality={building.sub_localities} building={building}/>
    </div>
    <section class='flex flex-col md:flex-row w-full md:items-center gap-2 md:gap-10'>
        <section class='w-full md:w-3/5 flex flex-col'>
            <div class="flex md:flex-row flex-col-reverse md:items-center md:gap-5">
                <h2 class='h2 font-bold transition-all duration-100'>{building.name}</h2>
                {#if !building.is_completed}
                    <div class="relative md:top-0.5 top-0 my-1 md:my-0">
                        <NotYetCompletedBadge/>
                    </div>
                {/if}
            </div>
            <p class='font-light mb-3 text-neutral-500'>
                <strong>{building.address + ', '}</strong> {building.neighbourhood + ', ' + building.sub_localities?.name + ', ' + building.localities?.name + ', ON' }
            </p>
        </section>
        <section class='w-full md:w-2/5 flex flex-col'>
            <p class='font-light flex flex-row items-center gap-5'>
                <AggregatedReviewScore reviewScore={buildingScore?.score || 0} size="medium"/>
                {buildingScore?.count || 0} {buildingScore?.count === 1 ? 'review' : 'reviews'}
            </p>
        </section>
    </section>
    <section class='flex flex-col md:flex-row items-start flex-nowrap justify-between gap-2 md:gap-10'>
        <section class='w-full md:w-3/5 flex flex-col mt-5 md:mt-0'>
            <figure class='inline w-full'>
                <img class='w-full h-[200px] md:h-[500px] rounded-2xl object-cover float-left mb-1 {buildingImage.isPlaceholder ? "grayscale-[85%]": ""}'
                     loading="lazy"
                     src='{buildingImage.url}'
                     alt='{buildingImage.isPlaceholder ? "A generic depiction of the city" : "The facade of " + building.name}'>
                {#if building.cover_image_credit}
                    <figcaption class='text-gray-400 -mb-2 text-xs text-right mr-4 pt-0.5 block'>Image courtesy
                        of {building.cover_image_credit}</figcaption>
                {/if}
            </figure>
            <section class='grid mt-5 md:hidden gap-5 md:gap-8 grid-cols-2 md:aspect-video'>
                {#each buildingDetailedScore as detailedScore}
                    <div class='flex flex-col justify-between'>
                        <span>{detailedScore.rating_types.external_label}</span>
                        <div class='flex items-center md:justify-start gap-3'>
                            <span
                                    class='font-bold'>{((detailedScore.score / detailedScore.rating_types.max_value) * 5).toFixed(1)}</span>
                            <div class='inline-block mb-0.5'>
                                <StarRating size={20}
                                            value={((detailedScore.score / detailedScore.rating_types.max_value) * 5).toFixed(1)}/>
                            </div>
                        </div>
                    </div>
                {/each}
            </section>
            {#if isAdmin}
                <section class="flex flex-col md:flex-row pt-5 -pb-5 gap-1">
                    {#if building.is_completed}
                        <button on:click={() => updateBuildingCompletionStatus(false)}
                                class="btn btn-xs text-xs variant-filled-warning">Mark Building As Under Construction
                        </button>
                    {:else}
                        <button on:click={() => updateBuildingCompletionStatus(true)}
                                class="btn btn-xs text-xs variant-filled-success">Mark Building As Completed
                        </button>
                    {/if}
                    <a href="/admin/buildings/images?uuid={building.uuid}" class="btn btn-xs text-xs variant-filled-success">Edit Building Image</a>
                </section>
            {/if}
            <section class='pt-10 w-full'>
                <h2 class='h2 text-xl font-bold'>Latest Reviews for
                    <InlineBuildingLabel {building}/>
                </h2>
                {#if reviews.length > 0}
                    {#each reviews as review, i}
                        <div class='my-1.5 scroll-mt-1' bind:this={reviewRefs[review.uuid]}>
                            <ReviewCard
                                    on:likeReview={handleLikeReview}
                                    on:unlikeReview={handleUnlikeReview}
                                    on:reportReview={handleReportReview}
                                    canReport={isLoggedIn}
                                    uuid='{review.uuid}'
                                    reviewScore='{review.review_scores?.score}'
                                    reviewer={reviewerNames[review.uuid]}
                                    commentary={review.review_rating_commentaries?.[0]}
                                    reviewRatings={review.review_ratings}
                                    livedFrom='{review.rental_profiles?.[0]?.lived_from}'
                                    livedUntil='{review.rental_profiles?.[0]?.lived_until}'
                                    likes={reviewLikes[review.uuid]?.count || 0}
                                    likedByCurrentUser={reviewLikes[review.uuid]?.isLikedByMe || false}
                                    isHighlighted={review.uuid === highlightedReview}
                                    url='{PUBLIC_URL}{$page.url.pathname}?rid={review.uuid}'
                                    isEarlyReview={i < 5}
                            />
                        </div>
                    {/each}

                    <p class='mb-2 py-2 {reviews.length < 5 ? "hidden md:block" : ""}'>
                        Lived in
                        <InlineBuildingLabel {building}/>
                        before? Leave a review and help a fellow future
                        tenant out!
                    </p>
                    <!-- hacky way of displaying two different buttons for mobile vs desktop-->
                    <div class="hidden md:block">
                        <AddReviewCTA buildingId={building.uuid}
                                      buildingName='{building.name}'
                                      textSize='text-base' buttonSize='btn-base'
                                      classes='w-full md:w-fit m-auto {reviews.length < 5 ? "hidden md:inline-block" : ""}'/>
                    </div>
                    <div class="md:hidden">
                        <AddReviewCTA buildingId={building.uuid}
                                      buildingName='{building.name}'
                                      isMobile="{true}"
                                      textSize='text-base' buttonSize='btn-base'
                                      classes='w-full md:w-fit m-auto {reviews.length < 5 ? "hidden md:inline-block" : ""}'/>
                    </div>
                {:else}
                    <section class="bg-neutral-100 mt-5 p-5 rounded-xl text-lg">
                        <p>
                            This building needs its first review! If you live at <span
                                class="font-bold">{building.name}</span> (or used to), why not
                            leave a review today?
                        </p>

                        <p class="mt-5">
                            Automatically earn our
                            <EarlyReviewerBadge/>
                            designation when you submit your review!
                        </p>

                        <!-- hacky way of displaying two different buttons for mobile vs desktop-->
                        <div class="hidden md:block">
                            <AddReviewCTA buildingId={building.uuid}
                                          buildingName='{building.name}'
                                          textSize='text-base' buttonSize='btn-base'
                                          classes='w-full md:w-fit m-auto mt-5'/>
                        </div>
                        <div class="md:hidden">
                            <AddReviewCTA buildingId={building.uuid}
                                          buildingName='{building.name}'
                                          isMobile="{true}"
                                          textSize='text-base' buttonSize='btn-base'
                                          classes='w-full md:w-fit m-auto mt-5'/>
                        </div>
                    </section>
                {/if}
            </section>
        </section>
        <section class='w-full md:w-2/5 flex flex-col mt-3 md:mt-0 gap-5'>
            <section class='hidden md:grid gap-5 md:gap-8 grid-cols-2 md:aspect-video'>
                {#each buildingDetailedScore as detailedScore}
                    <div class='flex flex-col justify-between'>
                        <span>{detailedScore.rating_types.external_label}</span>
                        <div class='flex items-center md:justify-start gap-3'>
                            <span
                                    class='font-bold'>{((detailedScore.score / detailedScore.rating_types.max_value) * 5).toFixed(1)}</span>
                            <div class='inline-block mb-0.5'>
                                <StarRating size={20}
                                            value={((detailedScore.score / detailedScore.rating_types.max_value) * 5).toFixed(1)}/>
                            </div>
                        </div>
                    </div>
                {/each}
            </section>
            {#if reviews.length < 5}
                <section class="mt-0 md:mt-10">
                    <MoreReviewsPrompt buildingId={building.uuid} buildingName={building.name}/>
                </section>
            {/if}
            <section class="mt-5 md:mt-10">
                <ReferToFriendAside referralCode={referralCode}
                                    url="{PUBLIC_URL}{$page.url.pathname}"/>
            </section>
        </section>
    </section>
    {#if nearbyBuildings.length > 0}
        <section class='mt-16'>
            <h2 class='h2 text-xl font-bold'>Nearby Buildings</h2>
            <section class='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-5'>
                {#each nearbyBuildings as nearbyBuilding}
                    <a href='/s/{nearbyBuilding.slug}'>
                        <BuildingCardV2
                                name={nearbyBuilding.name}
                                address='{nearbyBuilding.address}'
                                aggregatedScore={nearbyBuilding.review_score > 0 ? {score: nearbyBuilding.review_score}: null}
                                neighbourhood='{nearbyBuilding.neighbourhood}'
                        />
                    </a>
                {/each}
            </section>
        </section>
    {/if}
    <WhereToGoNext buildingId="{building.uuid}"
                   buildingImageUrl="{buildingImage.url}"
                   buildingName="{building.name}"
                   referralCode={referralCode}
                   url="{PUBLIC_URL}{$page.url.pathname}"/>
</div>
<!--{#if isAdmin}-->
<!--    <div class=''>-->
<!--        <h3 class='pt-20 '>Debug</h3>-->
<!--        <pre>{JSON.stringify(reviewerNames, null, 2)}</pre>-->
<!--        <pre class=''>{JSON.stringify(reviewLikes, null, 2)}</pre>-->
<!--        <pre class=''>{JSON.stringify(buildingDetailedScore, null, 2)}</pre>-->
<!--    </div>-->
<!--{/if}-->
